/**
 * Booking provider table — the single source of provider-specific knowledge.
 *
 * A CTA carries only a destination URL; the provider (Cal.com, Calendly, …) is
 * derived from that URL's host at runtime. Everything provider-specific —
 * embed-URL tuning, booking-success postMessage detection, and SDK overlay
 * opening — lives here, so adding a provider is one entry, not edits scattered
 * across the widget.
 */

import type { IntegrationsConfig } from '../../types/configs.generated';
import { logger } from '../core/logger';

type BookingIntegrationConfigKey = keyof Pick<IntegrationsConfig, 'calcom' | 'calendly'>;

export interface BookingProvider {
    /** Stable id for logging. */
    id: string;
    /** Matching integrations config key when this provider is configurable. */
    configKey?: BookingIntegrationConfigKey;
    /** Host suffix that identifies this provider (e.g. 'cal.com'). */
    host: string;
    /** Adjust the URL for a compact in-chat embed. Defaults to identity. */
    buildEmbedUrl?: (parsed: URL) => URL;
    /** Detect a booking-confirmation postMessage payload from this provider. */
    isBookingSuccess: (data: Record<string, unknown>) => boolean;
    /** Open the provider's SDK overlay. Returns false if the SDK is not loaded. */
    openOverlay: (parsed: URL) => boolean;
}

const CALCOM: BookingProvider = {
    id: 'calcom',
    configKey: 'calcom',
    host: 'cal.com',
    /**
     * Tune the Cal.com booking link for a compact in-chat iframe embed.
     * - host app.cal.com: the apex `cal.com` iframe is blocked on host sites whose
     *   CSP allows Cal via `frame-src https://*.cal.com` — the `*` wildcard does NOT
     *   match the bare apex, so the card renders blank/broken (e.g. userose.ai).
     *   app.cal.com serves the same booking page and IS covered by the wildcard.
     * - embed: serves Cal.com's compact embed layout (value = namespace, empty = default)
     * - column_view: single-column date picker + time slots (narrowest layout)
     * - hideEventTypeDetails: removes the event description sidebar
     * - theme=light: matches the chat UI
     */
    buildEmbedUrl(parsed) {
        const out = new URL(parsed.toString());
        if (out.hostname === 'cal.com' || out.hostname === 'www.cal.com') {
            out.hostname = 'app.cal.com';
        }
        out.searchParams.set('embed', '');
        out.searchParams.set('layout', 'column_view');
        out.searchParams.set('hideEventTypeDetails', 'true');
        out.searchParams.set('theme', 'light');
        return out;
    },
    isBookingSuccess(data) {
        return (
            data?.originator === 'CAL' && String(data?.type ?? '').startsWith('bookingSuccessful')
        );
    },
    openOverlay(parsed) {
        const win = window as unknown as Record<string, unknown>;
        if (!win.Cal) return false;
        const cal = win.Cal as (action: string, opts: Record<string, unknown>) => void;
        const calLink = parsed.pathname.replace(/^\//, '') + parsed.search;
        logger.debug('📅 Opening Cal.com modal (best-effort)', { calLink });
        cal('modal', { calLink });
        return true;
    },
};

const CALENDLY: BookingProvider = {
    id: 'calendly',
    configKey: 'calendly',
    host: 'calendly.com',
    isBookingSuccess(data) {
        return data?.event === 'calendly.event_scheduled';
    },
    openOverlay(parsed) {
        const win = window as unknown as Record<string, unknown>;
        if (!win.Calendly) return false;

        const existingOverlay = document.querySelector('[data-calendly-overlay]');
        if (existingOverlay) {
            logger.debug('📅 Calendly overlay already open, skipping');
            return true;
        }

        const calendly = win.Calendly as {
            initInlineWidget: (opts: { url: string; parentElement: HTMLElement }) => void;
        };
        logger.debug('📅 Opening Calendly inline widget in overlay', { url: parsed.toString() });

        // Build a custom overlay + container so we get popup UX
        // while using initInlineWidget for postMessage support
        const overlay = document.createElement('div');
        overlay.dataset.calendlyOverlay = 'true';
        overlay.style.cssText =
            'position:fixed;top:0;left:0;width:100%;height:100%;z-index:999999;background:rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;';

        const container = document.createElement('div');
        container.style.cssText =
            'width:min(100%,480px);height:min(100%,700px);background:white;border-radius:8px;overflow:hidden;position:relative;';

        const closeBtn = document.createElement('button');
        closeBtn.textContent = '✕';
        closeBtn.style.cssText =
            'position:absolute;top:8px;right:12px;z-index:10;background:none;border:none;font-size:20px;cursor:pointer;color:#666;padding:4px 8px;';
        const cleanup = () => {
            overlay.remove();
            document.removeEventListener('keydown', keyHandler);
        };
        const keyHandler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') cleanup();
        };
        document.addEventListener('keydown', keyHandler);
        closeBtn.addEventListener('click', cleanup);
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) cleanup();
        });

        container.appendChild(closeBtn);
        overlay.appendChild(container);
        document.body.appendChild(overlay);

        calendly.initInlineWidget({ url: parsed.toString(), parentElement: container });
        return true;
    },
};

const PROVIDERS: BookingProvider[] = [CALCOM, CALENDLY];

/** Host suffixes of all known booking providers (for postMessage origin checks). */
export const BOOKING_PROVIDER_HOSTS = PROVIDERS.map((p) => p.host);

/** Resolve the booking provider for a CTA URL by host, or null if none matches. */
export function resolveBookingProvider(url: string | null | undefined): BookingProvider | null {
    if (!url) return null;
    try {
        const host = new URL(url).hostname;
        return PROVIDERS.find((p) => host === p.host || host.endsWith(`.${p.host}`)) ?? null;
    } catch {
        return null;
    }
}

/** Whether a CTA URL points at a known booking provider. */
export function isBookingProviderUrl(url: string | null | undefined): boolean {
    return resolveBookingProvider(url) !== null;
}

/** Build the compact in-chat embed URL for a provider link (identity if untuned). */
export function buildBookingEmbedUrl(url: string): string {
    try {
        const parsed = new URL(url);
        const provider = resolveBookingProvider(url);
        return (provider?.buildEmbedUrl?.(parsed) ?? parsed).toString();
    } catch {
        return url;
    }
}

/** Whether a postMessage payload is a booking confirmation from any provider. */
export function isBookingSuccessMessage(data: Record<string, unknown>): boolean {
    return PROVIDERS.some((p) => p.isBookingSuccess(data));
}
